import * as stylex from '@stylexjs/stylex'
import { useEffect, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import Dialog from './Dialog'
import Button from './Button'
import { FormField, Input, Select, Textarea } from './FormControls'
import MoneyInput from './MoneyInput'
import ExpenseGuestSharing from './ExpenseGuestSharing'
import Icon, { type IconName } from './Icon'
import TransactionDateTimeField from './TransactionDateTimeField'
import { centsToInput, combineTransactionDateTime, currencyOptions, equalShares, findPerson, formatMoney, formatTransactionDate, parseCents, transactionDateInputValue, transactionTimeInputValue, type Expense, type Overview, type UpdateExpenseInput } from '../lib/ledger'


const styles = stylex.create({
  actions: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)', gap: '8px' },
  shares: { display: 'grid', gap: '8px', padding: '16px', borderRadius: '1rem', backgroundColor: '#fff', border: '1px solid #c6d2c1' },
  shareRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', paddingBlock: '8px', fontSize: '14px', overflowWrap: 'anywhere' },
  description: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#536158', fontSize: '14px', lineHeight: '1.5' },
  reverse: { borderTop: '1px solid #c6d2c1', paddingTop: '20px' },
  sharingSummary: { minHeight: '44px', paddingBlock: '12px', color: '#315c3d', fontSize: '14px', fontWeight: 600, cursor: 'pointer', ':focus-visible': { outline: '2px solid #397a4b', outlineOffset: '2px' } },
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
    borderRadius: "1rem",
    backgroundColor: "#f2f7f0",
    padding: "16px",
  },
  s2: {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    color: "#78907b",
  },
  s3: {
    overflowWrap: 'anywhere',
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 600,
    color: "#315c3d",
  },
  s4: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#718075",
  },
  s5: {
    display: "flex",
    height: "44px",
    width: "44px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    backgroundColor: "#fff",
    color: "#4b8b59",
  },
  s6: {
    height: "20px",
    width: "20px",
  },
  s7: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s8: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#748077",
  },
  s9: {
    position: "relative",
    marginTop: "8px",
  },
  s10: {
    marginLeft: "12px",
    display: "flex",
    alignItems: "center",
    color: "#4b8458",
  },
  s11: {
    height: "16px",
    width: "16px",
  },
  s12: {
    height: "100%",
    minWidth: "0px",
    flex: "1 1 0%",
    backgroundColor: "transparent",
    paddingInline: "10px",
    fontSize: "16px",
    color: "#46544a",
    outline: "none",
    "::placeholder": {
      color: "#99a099",
    },
  },
  s13: {
    paddingInline: "12px",
    color: "#748077",
  },
  s14: {
    position: "absolute",
    zIndex: "20",
    marginTop: "8px",
    maxHeight: "224px",
    width: "100%",
    overflowY: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    padding: "6px",
    boxShadow: "0 10px 24px rgba(48,70,51,.14)",
  },
  s15: {
    paddingInline: "12px",
    paddingBlock: "8px",
    fontSize: "12px",
    color: "#748077",
  },
  s16: {
    height: "16px",
    width: "16px",
    color: "#4b8458",
  },
  s17: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  s18: {
    fontSize: "14px",
    fontWeight: 600,
  },
  s19: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#3c6545",
  },
  s20: {
    marginTop: "12px",
    height: "192px",
    width: "100%",
  },
  s21: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    columnGap: "16px",
    rowGap: "8px",
  },
  s22: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: "#657168",
  },
  s23: {
    height: "8px",
    width: "8px",
    borderRadius: "9999px",
  },
  s24: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s25: {
    marginTop: "8px",
    minHeight: "96px",
    width: "100%",
    resize: "vertical",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s26: {
    marginTop: "8px",
    display: "flex",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#c8d9c6",
    backgroundColor: "#f8fbf7",
    paddingInline: "16px",
    paddingBlock: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#4b8458",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f7ee",
    },
  },
  s27: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
  },
  s28: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  s29: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.5rem",
    backgroundColor: "#f4f7f3",
    paddingInline: "12px",
    paddingBlock: "8px",
    fontSize: "12px",
    color: "#5e6d62",
  },
  s30: {
    height: "16px",
    width: "16px",
    color: "#568b60",
  },
  s31: {
    minWidth: "0px",
    flex: "1 1 0%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  s32: {
    minHeight: '44px',
    minWidth: '44px',
    color: "#88948a",
    ":hover": {
      color: "#a34d1b",
    },
  },
  s33: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: "10px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
  },
  s34: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.55,
    },
  },
  s35: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e2e9df",
    backgroundColor: "#fbfdfb",
    padding: "16px",
  },
  s36: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    '@media (min-width: 640px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' },
    gap: "12px",
  },
  s37: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#526157",
  },
  s38: {
    height: "42px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "12px",
    fontSize: "13px",
    fontWeight: 400,
    color: "#46544a",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s39: {
    fontSize: "12px",
    color: "#a34d1b",
  },
  s40: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    gap: "8px",
    borderWidth: 1,
    borderColor: "#eccbc5",
    borderRadius: "0.75rem",
    backgroundColor: "#fff7f5",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#a34d43",
    ":hover": { backgroundColor: "#fff0ed" },
    ":disabled": { cursor: "not-allowed", opacity: 0.55 },
  },
})

const categoryStyles = stylex.create({
  control: { display: 'flex', height: '44px', alignItems: 'center', borderRadius: '0.75rem', borderWidth: 1, backgroundColor: '#fff', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  open: { borderColor: '#5b9a6c', boxShadow: '0 0 0 4px color-mix(in srgb, #5b9a6c 10%, transparent)' },
  closed: { borderColor: '#dbe2d9' },
  option: { display: 'flex', width: '100%', alignItems: 'center', gap: '8px', borderRadius: '0.5rem', paddingInline: '12px', paddingBlock: '8px', textAlign: 'left', fontSize: '13px', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  selected: { backgroundColor: '#edf7eb', fontWeight: 600, color: '#3e7d4d' },
  idle: { color: '#46544a', ':hover': { backgroundColor: '#f5faf3' } },
})
type Props = { data: Overview; expense: Expense; fullscreen?: boolean; onClose: () => void; onReverse: () => Promise<void>; onSave: (input: UpdateExpenseInput) => Promise<void>; onSettlementConfirmed: () => Promise<void> }
const PIE_COLORS = ['#4d9360', '#c49760', '#7899a3', '#9a78a5', '#d27a62', '#6c8e75']
const EXPENSE_ICONS: Array<{ name: IconName; label: string }> = [
  { name: 'food', label: 'Food' },
  { name: 'rent', label: 'Rent' },
  { name: 'transport', label: 'Transport' },
  { name: 'shopping', label: 'Shopping' },
  { name: 'clothing', label: 'Clothing' },
  { name: 'entertainment', label: 'Entertainment' },
  { name: 'travel', label: 'Travel' },
  { name: 'utilities', label: 'Utilities' },
  { name: 'gift', label: 'Gift' },
  { name: 'receipt', label: 'Other' },
]

export default function ExpenseDetailDialog({ data, expense, fullscreen = false, onClose, onReverse, onSave, onSettlementConfirmed }: Props) {
  const [editing, setEditing] = useState(false)
  const [sharingOpen, setSharingOpen] = useState(false)
  const storageKey = `settleup.expense.${expense.id}.notes`
  const saved = typeof window === 'undefined' ? null : window.localStorage.getItem(storageKey)
  const initial = saved ? JSON.parse(saved) as { comment?: string; files?: string[]; icon?: IconName } : {}
  const initialDetails = { comment: expense.notes ?? initial.comment ?? '', files: initial.files ?? [], icon: (initial.icon ?? 'receipt') as IconName }
  const initialTitle = expense.description
  const initialAmount = centsToInput(expense.amountCents)
  const initialCurrency = expense.currency
  const expenseOccurredAt = expense.occurredAt ?? expense.createdAt
  const initialExpenseDate = transactionDateInputValue(expenseOccurredAt, Boolean(expense.occurredTimeSet))
  const initialExpenseTime = transactionTimeInputValue(expenseOccurredAt, Boolean(expense.occurredTimeSet))
  const [comment, setComment] = useState(initialDetails.comment)
  const [files, setFiles] = useState(initialDetails.files)
  const [icon, setIcon] = useState<IconName>(initialDetails.icon)
  const [savedDetails, setSavedDetails] = useState(initialDetails)
  const [title, setTitle] = useState(initialTitle)
  const [amount, setAmount] = useState(initialAmount)
  const [currency, setCurrency] = useState(initialCurrency)
  const [expenseDate, setExpenseDate] = useState(initialExpenseDate)
  const [expenseTime, setExpenseTime] = useState(initialExpenseTime)
  const [savingExpense, setSavingExpense] = useState(false)
  const [expenseError, setExpenseError] = useState('')
  const [reversing, setReversing] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [categoryQuery, setCategoryQuery] = useState('')
  useEffect(() => {
    const closeDropdown = (event: Event) => {
      const target = event.target
      if (!(target instanceof Element) || !target.closest('[data-dropdown]')) {
        setCategoryOpen(false)
      }
    }
    document.addEventListener('pointerdown', closeDropdown)
    document.addEventListener('click', closeDropdown)
    document.addEventListener('focusin', closeDropdown)
    return () => {
      document.removeEventListener('pointerdown', closeDropdown)
      document.removeEventListener('click', closeDropdown)
      document.removeEventListener('focusin', closeDropdown)
    }
  }, [])
  const selectedIcon = EXPENSE_ICONS.find((item) => item.name === icon) ?? EXPENSE_ICONS[EXPENSE_ICONS.length - 1]
  const filteredIcons = EXPENSE_ICONS.filter((item) => item.label.toLowerCase().includes(categoryQuery.toLowerCase()))
  function chooseCategory(item: { name: IconName }) {
    setIcon(item.name)
    setCategoryQuery('')
    setCategoryOpen(false)
  }
  const [savedState, setSavedState] = useState(false)
  const payer = findPerson(data, expense.paidById)
  const participants = expense.participantIds.map((id) => findPerson(data, id)).filter(Boolean)
  const showSplit = participants.length > 2
  const fallbackShares = equalShares(expense.amountCents, expense.participantIds.length)
  const chartData = expense.participantIds.map((id, index) => ({ id, name: id === data.currentUser.id ? 'You' : findPerson(data, id)?.name ?? 'Someone', value: expense.participantAmounts?.[id] ?? fallbackShares[index] ?? 0 }))

  const amountCents = parseCents(amount)
  const expenseInputValid = Boolean(title.trim()) && amountCents !== null && amountCents > 0 && Boolean(expenseDate)
  const expenseChanged = title.trim() !== initialTitle || amountCents !== expense.amountCents || currency !== initialCurrency || expenseDate !== initialExpenseDate || expenseTime !== initialExpenseTime || comment !== initialDetails.comment
  const detailsChanged = comment !== savedDetails.comment || icon !== savedDetails.icon || files.length !== savedDetails.files.length || files.some((file, index) => file !== savedDetails.files[index])
  const hasChanges = expenseChanged || detailsChanged
  function closeDetails() {
    if (editing && hasChanges && !window.confirm('Discard your unsaved expense changes?')) return
    onClose()
  }

  async function saveExpense() {
    if (!expenseInputValid || amountCents === null) {
      setExpenseError('Enter a title, a positive amount, and a date.')
      return false
    }
    if (savingExpense) return false
    setSavingExpense(true)
    setExpenseError('')
    try {
      await onSave({
        notes: comment.trim(),
        description: title.trim(),
        amountCents,
        currency,
        occurredAt: combineTransactionDateTime(expenseDate, expenseTime),
        occurredTimeSet: Boolean(expenseTime),
      })
      return true
    } catch (reason) {
      setExpenseError(reason instanceof Error ? reason.message : 'The expense could not be updated.')
      return false
    } finally {
      setSavingExpense(false)
    }
  }

  async function saveDetails() {
    if (!hasChanges || savingExpense) return
    if (expenseChanged && !await saveExpense()) return
    window.localStorage.setItem(storageKey, JSON.stringify({ comment, files, icon }))
    setSavedDetails({ comment, files: [...files], icon })
    setSavedState(true)
    window.setTimeout(() => setSavedState(false), 1800)
  }

  async function reverseExpense() {
    if (reversing || !window.confirm('Reverse this expense? The original record stays in history, but it will no longer affect balances.')) return
    setReversing(true)
    setExpenseError('')
    try {
      await onReverse()
      window.localStorage.removeItem(storageKey)
    } catch (reason) {
      setExpenseError(reason instanceof Error ? reason.message : 'The expense could not be reversed.')
      setReversing(false)
    }
  }

  return <Dialog fullscreen={fullscreen} description={editing ? 'Save changes to recalculate balances.' : undefined} closeDisabled={savingExpense || reversing} onClose={closeDetails} title={editing ? 'Edit expense' : expense.description} footer={editing ? <div {...stylex.props(styles.actions)}><Button disabled={savingExpense || reversing} onClick={closeDetails} variant="secondary">Cancel</Button><Button disabled={!hasChanges || !expenseInputValid || savingExpense || reversing} onClick={() => void saveDetails()}>{savedState ? 'Saved' : savingExpense ? 'Saving…' : 'Save changes'}</Button></div> : <Button fullWidth onClick={() => setEditing(true)}>Edit expense<Icon name="arrow-right" /></Button>}>
    <div {...stylex.props(styles.s0)}>
      <div {...stylex.props(styles.s1)}><div><p {...stylex.props(styles.s2)}>Expense total</p><p {...stylex.props(styles.s3)}>{formatMoney(expense.amountCents, expense.currency)}</p><p {...stylex.props(styles.s4)}>{expense.paidById === data.currentUser.id ? 'You paid' : `${payer?.name ?? 'Someone'} paid`} · {formatTransactionDate(expenseOccurredAt, Boolean(expense.occurredTimeSet))}</p></div><span {...stylex.props(styles.s5)}><Icon {...stylex.props(styles.s6)} name={selectedIcon.name} /></span></div>
      {!editing && <>
        <section {...stylex.props(styles.shares)}><h3 {...stylex.props(styles.s7)}>Each person's share</h3>{chartData.map((person) => <div {...stylex.props(styles.shareRow)} key={person.id}><span>{person.name}</span><strong>{formatMoney(person.value, expense.currency)}</strong></div>)}</section>
        {comment && <section><h3 {...stylex.props(styles.s7)}>Description</h3><p {...stylex.props(styles.description)}>{comment}</p></section>}
        <details onToggle={(event) => setSharingOpen(event.currentTarget.open)}><summary {...stylex.props(styles.sharingSummary)}>Payment links and requests</summary>{sharingOpen && <ExpenseGuestSharing data={data} expense={expense} onSettlementConfirmed={onSettlementConfirmed} />}</details>
      </>}
      {editing && <>
      <section {...stylex.props(styles.s35)}>
        <FormField label="What was it for?"><Input aria-label="Expense title" autoFocus disabled={savingExpense || reversing} maxLength={120} onChange={(event) => setTitle(event.target.value)} value={title} /></FormField>
        <div {...stylex.props(styles.s36)}>
          <FormField label="Amount"><MoneyInput aria-label="Expense amount" disabled={savingExpense || reversing} onValueChange={setAmount} value={amount} /></FormField>
          <FormField label="Currency"><Select aria-label="Expense currency" disabled={savingExpense || reversing} onChange={(event) => setCurrency(event.target.value)} value={currency}>{currencyOptions(currency).map((option) => <option key={option} value={option}>{option}</option>)}</Select></FormField>
        </div>
        <TransactionDateTimeField date={expenseDate} dateLabel="Expense date" onDateChange={setExpenseDate} onTimeChange={setExpenseTime} time={expenseTime} timeLabel="Expense time" />
        {expenseError && <p {...stylex.props(styles.s39)}>{expenseError}</p>}
      </section>
      <section><label {...stylex.props(styles.s7)} htmlFor="expense-category">Category</label><p {...stylex.props(styles.s8)}>Search and choose a category for this expense.</p><div {...stylex.props(styles.s9)} data-dropdown><div {...stylex.props(categoryStyles.control, categoryOpen ? categoryStyles.open : categoryStyles.closed)}><span {...stylex.props(styles.s10)}><Icon {...stylex.props(styles.s11)} name={selectedIcon.name} /></span><input aria-controls="expense-category-options" aria-expanded={categoryOpen} aria-label="Search categories" autoComplete="off" {...stylex.props(styles.s12)} id="expense-category" onChange={(event) => { setCategoryQuery(event.target.value); setCategoryOpen(true) }} onFocus={() => setCategoryOpen(true)} onKeyDown={(event) => { if (event.key === 'Enter' && filteredIcons[0]) { event.preventDefault(); chooseCategory(filteredIcons[0]) } }} placeholder={selectedIcon.label} value={categoryQuery} /><button aria-label="Toggle category options" {...stylex.props(styles.s13)} onClick={() => setCategoryOpen((open) => !open)} type="button">⌄</button></div>{categoryOpen && <div {...stylex.props(styles.s14)} id="expense-category-options" role="listbox">{filteredIcons.length === 0 ? <p {...stylex.props(styles.s15)}>No categories found.</p> : filteredIcons.map((item, index) => <button aria-selected={icon === item.name} {...stylex.props(categoryStyles.option, icon === item.name || index === 0 ? categoryStyles.selected : categoryStyles.idle)} key={item.name} onClick={() => chooseCategory(item)} role="option" type="button"><Icon {...stylex.props(styles.s16)} name={item.name} />{item.label}</button>)}</div>}</div></section>
      {showSplit && <section><h3 {...stylex.props(styles.s18)}>Split between {participants.length} people</h3><div {...stylex.props(styles.s20)}><ResponsiveContainer height="100%" width="100%"><PieChart><Pie data={chartData} dataKey="value" cx="50%" cy="50%" innerRadius={48} outerRadius={74} paddingAngle={2}>{chartData.map((item, index) => <Cell fill={PIE_COLORS[index % PIE_COLORS.length]} key={item.name} />)}</Pie><Tooltip formatter={(value) => formatMoney(Number(value), expense.currency)} /></PieChart></ResponsiveContainer></div><div {...stylex.props(styles.s21)}>{chartData.map((item, index) => <span {...stylex.props(styles.s22)} key={item.name}><span {...stylex.props(styles.s23)} style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />{item.name}</span>)}</div></section>}
      <FormField label="Description (optional)"><Textarea disabled={savingExpense || reversing} maxLength={2000} onChange={(event) => setComment(event.target.value)} placeholder="Add details about this expense" value={comment} /></FormField>
      <section><p {...stylex.props(styles.s7)}>Attachments</p><label {...stylex.props(styles.s26)}><Icon {...stylex.props(styles.s11)} name="plus" />Attach files<input {...stylex.props(styles.s27)} multiple onChange={(event) => setFiles((current) => [...current, ...Array.from(event.target.files ?? []).map((file) => file.name)])} type="file" /></label>{files.length > 0 && <ul {...stylex.props(styles.s28)}>{files.map((file, index) => <li {...stylex.props(styles.s29)} key={`${file}-${index}`}><Icon {...stylex.props(styles.s30)} name="receipt" /><span {...stylex.props(styles.s31)}>{file}</span><button aria-label={`Remove ${file}`} {...stylex.props(styles.s32)} onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))} type="button">×</button></li>)}</ul>}</section>
      </>}
      <section {...stylex.props(styles.reverse)}><Button disabled={reversing || savingExpense} onClick={() => void reverseExpense()} variant="danger">{reversing ? 'Reversing…' : 'Reverse expense'}</Button>{!editing && expenseError && <p role="alert" {...stylex.props(styles.s39)}>{expenseError}</p>}</section>
    </div>
  </Dialog>
}
